import { useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { OWN_CONSENTS_QUERY_KEY } from '@/constants/queryKeys';
import { useOwnClientProfileQuery, useOwnConsentsQuery } from '@/hooks/useClientPortal';
import { REQUIRED_CONSENTS_MISSING_EVENT_NAME } from '@/lib/consentGateEvents';
import { ConsentsDeclinedScreen } from './ConsentsDeclinedScreen';
import { isOnboardingComplete, listDeclinedRequiredConsents } from './consentGateStatus';
import { OnboardingForm } from './OnboardingForm';
import { OnboardingHeading } from './OnboardingHeading';
import { PortalGateHeader } from './PortalGateHeader';

export function RequireClientConsents() {
  const queryClient = useQueryClient();
  const profileQuery = useOwnClientProfileQuery();
  const consentsQuery = useOwnConsentsQuery();
  const [isReviewing, setIsReviewing] = useState(false);

  useEffect(
    function listenForRequiredConsentsMissing() {
      function refetchConsents() {
        void queryClient.invalidateQueries({ queryKey: OWN_CONSENTS_QUERY_KEY });
      }
      window.addEventListener(REQUIRED_CONSENTS_MISSING_EVENT_NAME, refetchConsents);
      return function cleanup() {
        window.removeEventListener(REQUIRED_CONSENTS_MISSING_EVENT_NAME, refetchConsents);
      };
    },
    [queryClient]
  );

  const profile = profileQuery.data;
  const consents = consentsQuery.data;

  if (profileQuery.isLoading === true || consentsQuery.isLoading === true) {
    return (
      <div className="min-h-screen bg-zoooffwhite flex items-center justify-center">
        <LoadingSpinner className="w-7 h-7" />
      </div>
    );
  }

  if (profile === undefined || consents === undefined) {
    return (
      <div className="min-h-screen bg-zoooffwhite flex items-center justify-center">
        <LoadingSpinner className="w-7 h-7" />
      </div>
    );
  }

  if (isOnboardingComplete(consents) === true) {
    return <Outlet />;
  }

  const declinedRequiredConsents = listDeclinedRequiredConsents(consents);

  return (
    <div className="min-h-screen bg-zoooffwhite">
      <PortalGateHeader />
      <main className="mx-auto px-4 py-6 sm:py-8 max-w-7xl">
        {declinedRequiredConsents.length > 0 && isReviewing === false ? (
          <ConsentsDeclinedScreen
            declinedRequiredConsents={declinedRequiredConsents}
            onReview={function reviewChoices() {
              setIsReviewing(true);
            }}
          />
        ) : (
          <>
            <OnboardingHeading fullName={profile.fullName} />
            <OnboardingForm
              profile={profile}
              currentConsents={consents}
              onFinished={function finished() {
                setIsReviewing(false);
              }}
            />
          </>
        )}
      </main>
    </div>
  );
}
